import {action, observable} from 'mobx';
import get from 'lodash/get';
import assign from 'lodash/assign';
import {AudioService, MuteAudioRoom} from '../../services/ts-audio/AudioService';
import {MessagesService} from '../../services/ts-messages-service/MessagesService.service';
import {TsSnapshotReminderService} from '../../services/ts-snapshot-reminder/TsSnapshotReminderService';
import {BandwidthMeasurementService} from '../../services/ts-bandwidth-measurement/ts-bandwidth-measurement.service';
import {getRootStore} from '../../app.bootstrap';
import {ITsWizardHelperService, TsWizardHelperService} from '../../services/ts-wizard-helper/ts-wizard-helper.service';
import {ScanService} from '@techsee/techsee-client-infra/lib/services/ScanService';
import {DeviceService} from '@techsee/techsee-client-infra/lib/services/DeviceService';
import TsScanService from '../../services/ts-scan/ts-scan.service';
import {ITsScanService} from '../../services/ts-scan/contracts';
import {TsScanArea} from '../../services/ts-scan-area/ts-scan-area.service';
import {TsSharingService} from '../../services/ts-sharing-service/ts-sharing.service';
import {ISharingService} from '../../services/ts-sharing-service/contracts';
import {IRoomService, RoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {HistoryService} from '@techsee/techsee-client-infra/lib/services/HistoryService';
import {ResourcesServices, IResourcesService} from '@techsee/techsee-client-infra/lib/services/ResourcesServices';
import {SessionSummaryLoggingService, ISessionSummaryLoggingService} from '../../services/SessionSummaryLoggingService';
import {RoomStorageService} from './_contracts/RoomStorage.service';
import {DashboardState} from './_contracts/DashboardState';
import {AgentSurveyService, IAgentSurveyService} from '@techsee/techsee-client-infra/lib/services/AgentSurveyService';
import {SwitchModeModalController} from '../../modals/switch-mode-modal/SwitchModeModal.controller';
import {AutoDataCollectionServices} from '@techsee/techsee-client-services/lib/services/AIAutoCollectionServices';
import {TsSmsService} from '../../services/ts-sms/ts-sms.service';
import {IRouteStore} from '../../router/routing.infra';
import {getAppTracer} from '../../../app.tracer';
import {DashboardAppMediaService} from '../../services/media-service-api/DashboardAppMediaService';
import {DashboardMainController} from '../../../states/dashboard/dashboard-main.controller';
import {
    DEFAULT_PEER_CONNECTIVITY_TIMEOUT_SECONDS,
    DEFAULT_VIDEO_WAIT_TIMEOUT_SECONDS,
    SessionClientRole
} from '@techsee/techsee-media-service/lib/MediaConstants';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {getDashboardLanguage} from '../helper/general.helper';

const trace = getAppTracer('DashboardStores');

const {
    BANDWIDTH_SAMPLES_BUFFER_MAX_SIZE,
    VIDEO_BANDWIDTH_SAMPLE_COUNT_DURING_SESSION,
    MEDIA_SERVICE
} = require('../../../states/dashboard/dashboard.settings');

export interface IDashboardStores {
    readonly audioService: AudioService;
    readonly messagesService: MessagesService;
    readonly snapshotReminderService: TsSnapshotReminderService;
    readonly bandwidthMeasurementService: BandwidthMeasurementService;
    readonly sharingService: ISharingService;
    readonly initDashboardServicesCompleted: Promise<void>;
    readonly wizardHelperService: ITsWizardHelperService;
    readonly scanService: ITsScanService;
    readonly deviceService: DeviceService;
    readonly tsScanArea: TsScanArea;
    readonly roomStorageService: RoomStorageService;
    readonly resourcesService: IResourcesService;
    readonly agentSurveyService: IAgentSurveyService;
    readonly roomService: IRoomService;
    readonly historyService: HistoryService;
    readonly autoDataCollectionServices: AutoDataCollectionServices;
    readonly smsService: TsSmsService;
}

export enum DashboardInitializationState {
    NOT_STARTED = 'not_started',
    INITIALIZING = 'initializing',
    COMPLETED = 'completed'
}

export interface InitStoreOptions {
    skipMediaService?: boolean;
}

let dashboardStoresInstance: DashboardStores | null = null;

export function setDashboardStore(store: DashboardStores): void {
    dashboardStoresInstance = store;
}

export function getDashboardStores(): DashboardStores {
    if (!dashboardStoresInstance) {
        throw new Error('DashboardStores not initialized. Call setDashboardStore() first.');
    }

    return dashboardStoresInstance;
}

export class DashboardStores implements IDashboardStores, IRouteStore {
    private _initState: DashboardInitializationState = DashboardInitializationState.NOT_STARTED;
    private _initDashboardServicesCompleted: Promise<void>;
    private _initPromiseResolver?: () => void;
    private _wizardHelperService: ITsWizardHelperService;
    private _roomService?: IRoomService;
    private _switchModeModalController?: SwitchModeModalController;
    private _historyService!: HistoryService;
    private _audioService!: AudioService;
    private _messagesService!: MessagesService;
    private _snapshotReminderService!: TsSnapshotReminderService;
    private _bandwidthMeasurementService!: BandwidthMeasurementService;
    private _tsScanService!: ITsScanService;
    private _deviceService!: DeviceService;
    private _tsScanArea?: TsScanArea;
    private _sharingService!: ISharingService;
    private _resourceService!: IResourcesService;
    private _roomStorageService!: RoomStorageService;
    private _autoDataCollectionServices!: AutoDataCollectionServices;
    private _agentSurveyService!: IAgentSurveyService;
    private _smsService!: TsSmsService;
    private _dashboardMediaService: any;
    private _dashboardMainController: any;

    // Audio config — computed during initStore
    private _isAudioEnabledForCurrentRoom: boolean = false;
    private _observerUser: string = '';
    private _enableAudioForObserver: boolean = false;

    public dashboardState: DashboardState;
    @observable public wizardState: any;
    public scanners: any;

    constructor(dashboardState: DashboardState) {
        this.dashboardState = dashboardState;
        this._wizardHelperService = new TsWizardHelperService();
        this._autoDataCollectionServices = new AutoDataCollectionServices();
        this._tsScanArea = new TsScanArea(getRootStore().localizationService, getRootStore().tsCanvasAnnotate);

        this._initDashboardServicesCompleted = new Promise<void>((resolve) => {
            this._initPromiseResolver = resolve as any;
        });
    }

    get clientRoomStorage() {
        return this.dashboardState.clientRoomStorage;
    }

    get dashboardMainController(): any {
        return this._dashboardMainController;
    }

    get dashboardMediaService(): any {
        return this._dashboardMediaService;
    }

    get mediaServiceType(): string {
        return (
            get(this.dashboardState.roomData, 'mediaServiceType') ||
            get(this.dashboardState.accountData, 'protectedSettings.mediaServiceType') ||
            ''
        );
    }

    private get accountData(): any {
        return this.dashboardState.accountData;
    }

    async initStore(options?: InitStoreOptions): Promise<void> {
        const rootStore = getRootStore();
        const roomData = this.dashboardState.roomData;
        const environmentDetect = rootStore.environmentService;

        this.dashboardState.useNewInterface = rootStore.tsInterfaceHelperService.useNewInterface;

        const localizationService = rootStore.localizationService;
        const accountLanguage = getDashboardLanguage(this.accountData, roomData);

        localizationService.setAccountData(this.accountData._id, accountLanguage);
        await localizationService.init();

        const mediaServiceType = this.mediaServiceType;

        if (mediaServiceType === MEDIA_SERVICE.OPENTOK && !environmentDetect.isIE11()) {
            const opentokVersion = get(this.accountData, 'protectedSettings.opentokVersion');

            await rootStore.domUtilsService.includeJsFile(`/js/opentok.${opentokVersion}.js`);
        }

        if (!options?.skipMediaService) {
            const webRTCSupport = await rootStore.webRtcDetector.getWebRtcInfo();

            rootStore.chatApi.setWebRTCSupport(webRTCSupport);
            rootStore.eventLogService.webRTCSupported(assign(webRTCSupport, {side: PlatformType.dashboard}));

            const dashboardMediaService = new DashboardAppMediaService(environmentDetect, webRTCSupport);

            await dashboardMediaService.initMediaService({
                clientRole: this.dashboardState.isObserve ? SessionClientRole.OBSERVER : SessionClientRole.AGENT,
                mediaServiceType: mediaServiceType as any,
                peerConnectivityTimeoutSeconds: DEFAULT_PEER_CONNECTIVITY_TIMEOUT_SECONDS,
                videoNotReceivedTimeoutSeconds:
                    get(this.accountData, 'protectedSettings.videoWaitTimeoutSeconds') ||
                    DEFAULT_VIDEO_WAIT_TIMEOUT_SECONDS
            });

            this._dashboardMediaService = dashboardMediaService;
        }

        // Audio config
        const isObserveUser = this.dashboardState.isObserve;

        this._enableAudioForObserver =
            mediaServiceType === MEDIA_SERVICE.MEDIASERVER || mediaServiceType === MEDIA_SERVICE.OPENTOK;
        this._isAudioEnabledForCurrentRoom = roomData.initiateWithAudio || this.dashboardState.audioRequestedFromUrl;

        if ((!this._enableAudioForObserver && isObserveUser) || environmentDetect.isIE11()) {
            this._isAudioEnabledForCurrentRoom = false;
        }

        this._observerUser = isObserveUser ? this.dashboardState.currentUser._id : '';

        await this.initDashboardServices();

        // Wizard state
        const roomId = this.dashboardState.sessionInfo.sessionRoomId || '';
        const displayTabletAsDesktop = rootStore.displayTabletAsDesktop;

        this.wizardState = this._wizardHelperService.getState(roomId);
        this.wizardState.showWizard =
            this.accountData.settings &&
            this.accountData.settings.showWizard &&
            !environmentDetect.isMobile(displayTabletAsDesktop) &&
            this.dashboardState.currentUser.showWizard &&
            !this.dashboardState.offlineMode &&
            !roomData.offline;

        this._sharingService.init(roomData._id);

        this.scanners = this._tsScanService.getScanners(this.accountData, roomData);

        if (!options?.skipMediaService) {
            this._dashboardMainController = new DashboardMainController(
                this.dashboardState,
                rootStore.tsReactStateHelper,
                this.dashboardState.currentUser,
                this.accountData,
                this.dashboardState.roomData,
                rootStore.browserUtilsService,
                this.wizardState,
                this.dashboardState.preloadedVideo,
                this.dashboardState.allowScreenShare,
                this.mediaServiceType,
                this._dashboardMediaService,
                this.scanners
            );
        }
    }

    @action
    private async initDashboardServices(): Promise<void> {
        if (this._initState === DashboardInitializationState.INITIALIZING) {
            return this._initDashboardServicesCompleted;
        }

        this._initState = DashboardInitializationState.INITIALIZING;
        const rootStore = getRootStore();

        try {
            if (this._dashboardMediaService) {
                this.initAudioService();
            }

            this._roomStorageService = new RoomStorageService(
                rootStore.chatApi,
                this.dashboardState,
                this.clientRoomStorage
            );
            this._messagesService = new MessagesService(rootStore.chatApi);
            this._snapshotReminderService = new TsSnapshotReminderService();
            this.initBandwidthMeasurementService();
            this._initState = DashboardInitializationState.COMPLETED;
            this._initPromiseResolver?.();

            this._smsService = new TsSmsService();

            this._resourceService = new ResourcesServices(
                rootStore.urlConfigService.get('API_URL'),
                () => rootStore.authService.getAuthToken() || ''
            );

            this._agentSurveyService = new AgentSurveyService(
                rootStore.urlConfigService.get('API_URL'),
                () => rootStore.authService.getAuthToken() || ''
            );

            this._roomService = new RoomService(
                rootStore.urlConfigService.get('API_URL'),
                () => rootStore.authService.getAuthToken() || ''
            );
            this._historyService = new HistoryService(
                rootStore.urlConfigService.get('API_URL'),
                () => rootStore.authService.getAuthToken() || ''
            );

            this._switchModeModalController = new SwitchModeModalController();

            this.setScanService();
            this.setDeviceService();
            this.setSharingService();

            rootStore.authService.on('newToken', () => {
                if (this._tsScanService) {
                    this.setScanService();
                }
                if (this._deviceService) {
                    this.setDeviceService();
                }
                if (!this._sharingService) {
                    this.setSharingService();
                }
            });
        } catch (error) {
            console.error('Failed to initialize dashboard services:', error);
            throw error;
        }
    }

    private initAudioService(): void {
        const rootStore = getRootStore();
        const muteAudio: MuteAudioRoom = (roomId: string, params: any) =>
            rootStore.roomService.setMuteAudio(roomId, params.data);

        this._audioService = new AudioService(
            this.accountData,
            rootStore.chatApi,
            rootStore.eventLogService,
            rootStore.localizationService.translate,
            this._dashboardMediaService,
            this._isAudioEnabledForCurrentRoom,
            this._observerUser,
            muteAudio,
            this._enableAudioForObserver,
            rootStore.environmentService,
            rootStore.displayTabletAsDesktop
        );
    }

    private initBandwidthMeasurementService(): void {
        this._bandwidthMeasurementService = new BandwidthMeasurementService(
            this.accountData.protectedSettings.enableNetworkBandwidthMeasurement,
            this.accountData.protectedSettings.minimalVideoBandwidth,
            BANDWIDTH_SAMPLES_BUFFER_MAX_SIZE,
            this.accountData.protectedSettings.videoBandwidthSampleCountDuringSession ||
                VIDEO_BANDWIDTH_SAMPLE_COUNT_DURING_SESSION
        );
    }

    private setSharingService = (): void => {
        const rootStore = getRootStore();
        const historyService = this._historyService;

        const roomService = new RoomService(
            rootStore.urlConfigService.get('API_URL'),
            () => rootStore.authService.getAuthToken() || ''
        );

        if (!this._sharingService) {
            this._sharingService = new TsSharingService(
                historyService,
                roomService,
                rootStore.chatApi,
                rootStore.tsCanvasAnnotate,
                rootStore.environmentService,
                rootStore.eventLogService,
                this.accountData,
                this.dashboardState
            );
        } else {
            this._sharingService.updateHistoryService(historyService);
            this._sharingService.updateRoomService(roomService);
        }
    };

    private setScanService = (): void => {
        const rootStore = getRootStore();
        const scanService = new ScanService(
            rootStore.urlConfigService.get('API_URL'),
            () => rootStore.authService.getAuthToken() || ''
        );

        if (!this._tsScanService) {
            this._tsScanService = new TsScanService(rootStore.tsCanvasAnnotate, scanService);
        } else {
            this._tsScanService.updateScanService(scanService);
        }
    };

    createSessionSummaryLoggingService(roomId: string): ISessionSummaryLoggingService {
        return new SessionSummaryLoggingService(getRootStore().eventsLogService, roomId);
    }

    private setDeviceService = (): void => {
        const rootStore = getRootStore();

        this._deviceService = new DeviceService(
            rootStore.urlConfigService.get('API_URL'),
            () => rootStore.authService.getAuthToken() || ''
        );
    };

    get resourcesService(): IResourcesService {
        return this._resourceService;
    }

    get audioService(): AudioService {
        if (!this._audioService) {
            throw new Error('AudioService not initialized. Call initStore() first.');
        }

        return this._audioService;
    }

    get messagesService(): MessagesService {
        if (!this._messagesService) {
            throw new Error('MessagesService not initialized. Call initStore() first.');
        }

        return this._messagesService;
    }

    get snapshotReminderService(): TsSnapshotReminderService {
        if (!this._snapshotReminderService) {
            throw new Error('TsSnapshotReminderService not initialized. Call initStore() first.');
        }

        return this._snapshotReminderService;
    }

    get switchModeModalController(): SwitchModeModalController {
        if (!this._switchModeModalController) {
            throw new Error('switchModeModalController not initialized. Call initStore() first.');
        }

        return this._switchModeModalController;
    }

    get bandwidthMeasurementService(): BandwidthMeasurementService {
        if (!this._bandwidthMeasurementService) {
            throw new Error('BandwidthMeasurementService not initialized. Call initStore() first.');
        }

        return this._bandwidthMeasurementService;
    }

    get sharingService(): ISharingService {
        if (!this._sharingService) {
            throw new Error('SharingService not initialized. Call initStore() first.');
        }

        return this._sharingService;
    }

    get roomService(): IRoomService {
        if (!this._roomService) {
            throw new Error('RoomService not initialized. Call initStore() first.');
        }

        return this._roomService;
    }

    get historyService(): HistoryService {
        if (!this._historyService) {
            throw new Error('HistoryService not initialized. Call initStore() first.');
        }

        return this._historyService;
    }

    get wizardHelperService(): ITsWizardHelperService {
        if (!this._wizardHelperService) {
            throw new Error('WizardHelperService not initialized. Call initStore() first.');
        }

        return this._wizardHelperService;
    }

    get initDashboardServicesCompleted(): Promise<void> {
        return this._initDashboardServicesCompleted;
    }

    get isInitialized(): boolean {
        return this._initState === DashboardInitializationState.COMPLETED;
    }

    get scanService(): ITsScanService {
        if (!this._tsScanService) {
            throw new Error('ScanService not initialized. Call initStore() first.');
        }

        return this._tsScanService;
    }

    get deviceService(): DeviceService {
        if (!this._deviceService) {
            throw new Error('DeviceService not initialized. Call initStore() first.');
        }

        return this._deviceService;
    }

    get tsScanArea(): TsScanArea {
        if (!this._tsScanArea) {
            throw new Error('tsScanArea not initialized');
        }

        return this._tsScanArea;
    }

    get roomStorageService(): RoomStorageService {
        if (!this._roomStorageService) {
            throw new Error('RoomStorageService not initialized. Call initStore() first.');
        }

        return this._roomStorageService;
    }

    get agentSurveyService(): IAgentSurveyService {
        if (!this._agentSurveyService) {
            throw new Error('AgentSurveyService not initialized. Call initStore() first.');
        }

        return this._agentSurveyService;
    }

    get autoDataCollectionServices(): AutoDataCollectionServices {
        if (!this._autoDataCollectionServices) {
            throw new Error('AutoDataCollectionService not initialized. Call initStore() first.');
        }

        return this._autoDataCollectionServices;
    }

    get smsService(): TsSmsService {
        if (!this._smsService) {
            throw new Error('SmsService not initialized. Call initStore() first.');
        }

        return this._smsService;
    }

    dispose(): void {
        trace.info('Disposing dashboard stores');

        try {
            if (this._dashboardMainController?.dispose) {
                this._dashboardMainController.dispose();
            }
        } catch (err) {
            trace.warn('Error disposing controller', err);
        }

        try {
            getRootStore().chatApi.disconnect();
        } catch (err) {
            trace.warn('Error disconnecting chatApi', err);
        }

        try {
            if (this._dashboardMediaService) {
                this._dashboardMediaService.dispose();
            }
        } catch (err) {
            trace.warn('Error disposing dashboardMediaService', err);
        }

        try {
            if (this._messagesService) {
                this._messagesService.dispose();
            }
        } catch (err) {
            trace.warn('Error disposing messagesService', err);
        }

        try {
            if (this._roomStorageService) {
                this._roomStorageService.dispose();
            }
        } catch (err) {
            trace.warn('Error disposing roomStorageService', err);
        }

        trace.info('Dashboard stores disposed');
        dashboardStoresInstance = null;
    }
}
